import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { MenuIcon } from "@inthememory/spectra-ui-icons";
import {
  Avatar,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@inthememory/spectra-ui";

import { useAuth } from "@/hooks/useAuth";
import { switchRole } from "@/lib/api";
import { getRuntimeConfig } from "@/lib/runtime-config";

const DEV_ROLES = ["viewer", "admin"] as const;

export function UserMenu() {
  const { t } = useTranslation();
  const { user, isLoading, isError, handleLogout } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { devMode } = getRuntimeConfig();

  if (isLoading || isError || !user) return null;

  const currentRole = user.roles[0] ?? "";

  const handleRoleSwitch = async (role: string) => {
    if (role === currentRole) return;
    const updated = await switchRole(role);
    queryClient.setQueryData(["me"], updated);
    queryClient.removeQueries({ queryKey: ["embed"] });
    navigate({ to: "/" });
  };

  const initials =
    user.email
      ?.split("@")[0]
      ?.split(".")
      .map((w: string) => w[0]?.toUpperCase())
      .join("") ?? "U";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-3 rounded-full border border-slate-200 bg-white py-1.5 pl-3 pr-1.5 text-sm transition hover:shadow-md">
          <MenuIcon className="h-4 w-4 text-slate-900" />
          <Avatar fallback={initials} size="sm" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" width={56}>
        <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
        {devMode && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t("dev.switchRole")}</DropdownMenuLabel>
            <DropdownMenuRadioGroup value={currentRole} onValueChange={handleRoleSwitch}>
              {DEV_ROLES.map((role) => (
                <DropdownMenuRadioItem key={role} value={role}>
                  {role}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout}>
          {t("auth.logout")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
